import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatDateRange } from "../formatters";
import "dayjs/locale/es";
import {getWeekNumber, toFirstDayOfWeek, getStartOfWeekByNumber} from "../../../utils/date-utils";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);
dayjs.locale("es");

interface WeekFilterProps {
  onWeekSelect: (week: number | null, startDay: string) => void;
  onRequestData?: (week: number, startDay: string) => void;
  dateRange: string | null;
  setDateRange: (range: string) => void;
  year: number;
  setYear: (year: number) => void;
  initialStartDate?: Dayjs | null;
  initialEndDate?: Dayjs | null;
  disabled?: boolean; // Nueva prop
}

const WeekFilter: React.FC<WeekFilterProps> = ({
  onWeekSelect,
  dateRange,
  setDateRange,
  setYear,
  initialStartDate,
  initialEndDate,
  disabled,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [range, setRange] = useState<{ start: Dayjs; end: Dayjs } | null>(null);
  const theme = useTheme();

  // selectedWeek se deriva del range para evitar inconsistencias entre estados
  const selectedWeek = range ? getWeekNumber(range.start) : null;

  const getDateRange = (startDate: Dayjs, endDate: Dayjs): string => `${startDate.format("YYYY-MM-DD")} / ${endDate.format("YYYY-MM-DD")}`;
  const getWeekCount = (): number => {
    let weekCount = 52;
    if(initialEndDate){
      let lastDateOfYear = dayjs(`${initialEndDate.year()}-12-31`);
      weekCount = getWeekNumber(lastDateOfYear);
    }
    return weekCount;
  }
  const weekOptions = Array.from({ length: getWeekCount() }, (_, i) => ({
    id: i + 1,
    name: `Semana ${i + 1}`,
  }));

  const rangeFor = (date: Dayjs): { start: Dayjs; end: Dayjs } => {
    const start = toFirstDayOfWeek(date);
    const end = start.add(6, "day").endOf("day");
    return { start, end };
  };

  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      const weekNumber = getWeekNumber(initialStartDate);
      setSelectedDate(initialStartDate);
      setRange({ start: initialStartDate, end: initialEndDate });
      setDateRange(getDateRange(initialStartDate, initialEndDate));
      setYear(initialStartDate.year());
      onWeekSelect(weekNumber, "Wednesday");
    } else {
      const today = dayjs();
      const initialRange = rangeFor(today);
      const weekNumber = getWeekNumber(initialRange.start);
      setSelectedDate(today);
      setRange(initialRange);
      setDateRange(getDateRange(initialRange.start, initialRange.end));
      setYear(initialRange.start.year());
      onWeekSelect(weekNumber, "Wednesday");
    }
  }, [initialStartDate, initialEndDate]);

  const handleWeekChange = (_event: any, newValue: any) => {
    const week = newValue ? newValue.id : null;
    if (week && week !== selectedWeek) {
      const currentYear = dayjs().year();
      const thursday = getStartOfWeekByNumber(currentYear, week);
      const newRange = { start: thursday, end: thursday.add(6, "day").endOf("day") };
      setRange(newRange);
      setDateRange(getDateRange(newRange.start, newRange.end));
      setYear(thursday.year());
      onWeekSelect(week, "Wednesday");
    }
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const newRange = rangeFor(newValue);
      const weekNumber = getWeekNumber(newRange.start);
      setSelectedDate(newValue);
      setRange(newRange);
      setDateRange(getDateRange(newRange.start, newRange.end));
      onWeekSelect(weekNumber, "Thursday");
    }
  };

  const isInSelectedWeek = (day: Dayjs) => {
    if (!range) return false;
    const startDate = range.start.startOf("day");
    const endDate = range.end.startOf("day");
    const currentDate = day.startOf("day");

    return (
      currentDate.unix() >= startDate.unix() &&
      currentDate.unix() <= endDate.unix()
    );
  };

  return (
    <>
      <Grid
        container
        direction="column"
        spacing={2}
        sx={{
          background: theme.palette.secondary.light,
          borderRadius: "10px",
          padding: "15px 15px",
          marginLeft: "2px",
          maxWidth: "375px",
          minWidth: "375px",
        }}
      >
        <Grid item xs={6}>
          <Autocomplete
            options={weekOptions}
            getOptionLabel={(option) => option.name}
            value={
              weekOptions.find((option) => option.id === selectedWeek) || null
            }
            onChange={handleWeekChange}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Semana"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            )}
            disabled={disabled}
          />
        </Grid>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <Grid item xs={6}>
            <DatePicker
              label="Seleccionar Día"
              value={selectedDate}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              slots={{
                textField: (props) => (
                  <TextField
                    {...props}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                ),
              }}
              slotProps={{
                day: (ownerState) => {
                  const isSelected = isInSelectedWeek(ownerState.day);
                  const startOfWeek = range?.start.startOf("day");
                  const endOfWeek = range?.end.startOf("day");

                  let borderRadius = {};

                  if (isSelected) {
                    if (
                      startOfWeek &&
                      ownerState.day.unix() === startOfWeek.unix()
                    ) {
                      borderRadius = {
                        borderTopLeftRadius: "50%",
                        borderBottomLeftRadius: "50%",
                      };
                    }
                    if (
                      endOfWeek &&
                      ownerState.day.unix() === endOfWeek.unix()
                    ) {
                      borderRadius = {
                        borderTopRightRadius: "50%",
                        borderBottomRightRadius: "50%",
                      };
                    }
                  }

                  return {
                    sx: isSelected
                      ? {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          borderRadius: 0,
                          ...borderRadius,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                          "&:focus": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }
                      : {},
                  };
                },
              }}
              sx={{
                "& .MuiPickersDay-root": {
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            />
          </Grid>
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        {range && (
          <Typography
            variant="subtitle2"
            sx={{ marginTop: 2, fontWeight: "bold" }}
          >
            Semana seleccionada: {selectedWeek} ({formatDateRange(dateRange!)})
          </Typography>
        )}
      </Grid>
    </>
  );
};

export default WeekFilter;
